// Core React Imports
import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

// Local Imports
import ConstantsPages from "../constants/ConstantsPages";
import UtilsUnit from "../utils/UtilsUnit";
import ConstantsParams from "../constants/ConstantsParams";
import useUtilsUnit from "../utils/useUtilsUnit";

const ProtectedRoute = ({ children, requiredRole = null }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userData, loginTimestamp } = useUtilsUnit({ userData: true, loginTimestamp: true });

  const checkAuthorization = useCallback(() => {
    // Check if login has expired
    if (UtilsUnit.isLoginExpired(loginTimestamp)) {
      UtilsUnit.handleLogout(dispatch);
      return false;
    }

    // First check basic authentication
    if (!UtilsUnit.isUserAuthenticated(userData)) {
      return false;
    }

    // If no specific role is required, just being authenticated is enough
    if (!requiredRole) {
      return true;
    }

    // Check role-based authorization
    if (requiredRole === ConstantsParams.UserTypeTerminalStatus.EDITOR) {
      // For EDITOR role requirement, allow EDITOR (2) and above (MANAGER = 3)
      return UtilsUnit.editorUserAbove(userData);
    }

    return false; // Default to unauthorized for any other role
  }, [userData, loginTimestamp, dispatch, requiredRole]);

  // Check authorization to control rendering
  const isAuthorized = checkAuthorization();

  useEffect(() => {
    if (!isAuthorized) {
      navigate(ConstantsPages.PAGE_WELCOME, { replace: true });
    }
  }, [isAuthorized, navigate]);

  // Only render children if authorized
  if (!isAuthorized) {
    return null; // Don't render anything while redirecting
  }

  return children;
};

export default ProtectedRoute;
